import animate, { Transition } from '@play-co/timestep-core/lib/animate';
import { animDuration, waitForItPromise } from 'src/lib/utils';
import View from '@play-co/timestep-core/lib/ui/View';
import ImageView, {
  ImageViewOpts,
} from '@play-co/timestep-core/lib/ui/ImageView';
import QuickViewPool from '@play-co/timestep-core/lib/ui/ViewPool2';
import sounds from 'src/lib/sounds';
import soundConfig from 'src/conf/soundConfig';
import { getRandomItemFromArray } from 'src/replicant/utils/random';

const EASE_SELECTION = [
  animate.linear,
  animate.easeInQuad,
  animate.easeInCubic,
  animate.easeInQuint,
];

const imageViewPool = new QuickViewPool(() => new ImageView());

function animateToIcon({
  superview,
  image,
  start,
  end,
  sfxBegin,
  sfxFinish,
  delay,
  easeSelection,
  duration,
}: ParticleEffectOpts & { delay: number }) {
  const imageToAnimate = imageViewPool.obtainView();

  const imageOpts = typeof image === 'string' ? { image } : image;

  imageToAnimate.updateOpts({
    centerAnchor: true,
    superview,
    x: start.position.x,
    y: start.position.y,
    scaleX: start.scale,
    scaleY: start.scale,
    zIndex: 90000 + delay,
    opacity: 1,
    width: 110,
    height: 110,
    offsetX: -55,
    offsetY: -55,
    scale: 0,
    ...imageOpts,
  });

  const cleanup = () => {
    imageToAnimate.removeFromSuperview();
    imageViewPool.releaseView(imageToAnimate);
  };

  duration = duration ?? animDuration;
  const animTime = duration * 10 * 0.25;

  animate(imageToAnimate)
    .wait(delay)
    .then(() => sfxBegin && sounds.playSound(sfxBegin))
    .then({ scale: 0.75 }, 0)
    .then(
      { x: end.position.x, scaleX: end.scale, scaleY: end.scale },
      animTime,
      animate.linear,
    )
    .then(() => sfxFinish && sounds.playSound(sfxFinish));

  animate(imageToAnimate, 'secondary')
    .wait(delay)
    .then(
      { y: end.position.y, scaleX: end.scale, scaleY: end.scale },
      animTime,
      getRandomItemFromArray(easeSelection ?? EASE_SELECTION, Math.random),
    )
    .then({ scale: 0 }, 100, animate.easeOut)
    .then(cleanup);

  return delay + animTime + 50;
}

type Point = {
  x: number;
  y: number;
};

export type ParticleStreamNode = { position: Point; scale: number };

export type ParticleEffectOpts = {
  superview: View;
  image: string | ImageViewOpts;
  count: number;
  start: ParticleStreamNode;
  end: ParticleStreamNode;
  sfxBegin?: keyof typeof soundConfig.sfx;
  sfxFinish?: keyof typeof soundConfig.sfx;
  delay?: number;
  easeSelection?: Transition[];
  duration?: number;
};

// Creates a particle stream of an icon with count items
// that runs between two points. This was quickly ripped off
// from Everwing Arcade. It was based on even older code.
export async function particleStream(opts: ParticleEffectOpts) {
  const { count } = opts;
  if (count <= 0) {
    return Promise.resolve();
  }

  const animationTime = opts.duration ?? animDuration;
  const itemDelay = animationTime / 4;

  let duration: number = 0;
  for (let i = 0; i < count; i++) {
    duration = animateToIcon({
      ...opts,
      duration: animationTime, // Todo: investigate why animateToIcon duration is still undefined despite the fallback
      delay: i * itemDelay + animationTime,
    });
  }

  await waitForItPromise(duration);
}
